/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import { Button } from '@chakra-ui/react';
import Card from "components/card";
import axios from 'axios';
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";

interface Props {
    isOpen: boolean;
    onClose(): void;
    tradeDetail: any;
    offerDetail: any;
    setSwalProps?: any;
    setOfferInfo?: any;
}

const CancelOfferModal: React.FC<Props> = ({
    isOpen,
    onClose,
    tradeDetail,
    offerDetail,
    setSwalProps,
    setOfferInfo,
}) => {
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

    const handleSubmit = async () => {
        if (!isConfirmed) return;

        setIsProcessing(true);
        try {
            await axios
                .post(process.env.REACT_APP_API_URL + `/api/${localStorage.getItem("userRole").toLowerCase()}/trade/action/trade-update-offer-status`, {
                    offer_id: offerDetail?.offer_id,
                    trade_id: tradeDetail.id,
                    trade_shortid: tradeDetail.shortid,
                    ticket_id: tradeDetail.ticket_id,
                    data: {
                        offerId: offerDetail?.offer_id
                    },
                    status: "cancelled"
                })
                .then(function (response) {
                    if (response && response.data?.status === "ok") {
                        if (setOfferInfo) {
                            setOfferInfo(response.data);
                        }

                        closeModal();

                        if (setSwalProps) {
                            setSwalProps({
                                show: true,
                                icon: 'success',
                                title: 'Success',
                                html: `Your offer has been cancelled.`,
                                showConfirmButton: true,
                                didClose: () => {
                                    setSwalProps({});
                                },
                            });
                        }
                    } else {
                        setIsProcessing(false);
                        setSwalProps({
                            show: true,
                            icon: "error",
                            title: "Oops!",
                            html: response.data?.message || 'An error occurred while submitting your offer.',
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    }
                })
                .catch(function (error) {
                    let errorMessage = "Offer cancelling has failed";
                    if (
                        error.response &&
                        (error.response.status === 400 || error.response.status === 500)
                    ) {
                        errorMessage = error.response.data.message;
                    }

                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: errorMessage,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                },
            });
        }
    };

    const closeModal = async () => {
        setIsProcessing(false);
        setIsConfirmed(false);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={closeModal}
            closeOnOverlayClick={false}
        >
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto w-full max-w-[500px] px-4 md:top-[12vh]">
                <ModalBody className="p-0 !text-navy-700">
                    <Card extra="px-6 pt-8 pb-10 flex flex-col !z-[1004]">
                        <h1 className="mb-[20px] text-2xl font-bold">Cancel Offer Confirmation</h1>
                        <div className="mb-[20px] text-sm">
                            <div className="mb-4">
                                You're about to cancel current offer. This action cannot be undone.
                            </div>
                            <div className="mb-1">
                                <span>Sell:</span>
                                <span className="ml-3 font-semibold">
                                    <DisplayAmountWithDecimals
                                        amount={Number(offerDetail.sell)}
                                        currency={offerDetail.sell_asset}
                                        decimal={tradeDetail?.from_asset_decimal}
                                    />
                                    &nbsp;{offerDetail.sell_asset}
                                </span>
                            </div>
                            <div className="mb-1">
                                <span>Receive:</span>
                                <span className="ml-3 font-semibold">
                                    <DisplayAmountWithDecimals
                                        amount={Number(offerDetail.receive)}
                                        currency={offerDetail.receive_asset}
                                        decimal={tradeDetail?.to_asset_decimal}
                                    />
                                    &nbsp;{offerDetail.receive_asset}
                                </span>
                            </div>
                            <div className="mb-1">
                                <span>Rate:</span>
                                <span className="ml-3 font-semibold">
                                    <DisplayAmountWithDecimals
                                        amount={Number(offerDetail.rate)}
                                        currency={offerDetail.sell_asset}
                                        decimal={tradeDetail?.from_asset_decimal}
                                    />
                                    &nbsp;{offerDetail.sell_asset}
                                </span>
                            </div>
                            {tradeDetail.from_network === "fiat" &&
                                <div className="mb-1">
                                    <span>Settlement Address:</span>
                                    <span className="ml-3 font-semibold">
                                        {tradeDetail?.to_address?.address.slice(0, 15)}...{tradeDetail?.to_address?.address.slice(-6)}
                                    </span>
                                </div>
                            }
                        </div>

                        <div className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-white/10 p-4 mb-4">
                            <input
                                type="checkbox"
                                id="confirmCancel"
                                checked={isConfirmed}
                                onChange={(e) => setIsConfirmed(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <label htmlFor="confirmCancel" className="text-sm text-navy-700 dark:text-white cursor-pointer">
                                I have carefully reviewed the offer details above and confirm that I want to cancel this offer. I understand that this action cannot be undone.
                            </label>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <Button
                                onClick={() => closeModal()}
                                isDisabled={isProcessing}
                                className="btn btn-default"
                            >
                                Not now
                            </Button>
                            <Button
                                onClick={() => handleSubmit()}
                                isLoading={isProcessing}
                                loadingText='Processing...'
                                isDisabled={!isConfirmed || isProcessing}
                                className="btn text-white bg-red-600 hover:bg-red-500">
                                Cancel offer
                            </Button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CancelOfferModal;